import { TestDefinition, QuestionType } from '../../types';

/**
 * 16 Personality Test (Myers-Briggs Type Indicator adaptado)
 *
 * Evalúa 4 dimensiones de personalidad:
 * - E/I: Extraversión vs Introversión
 * - S/N: Sensación vs Intuición
 * - T/F: Pensamiento vs Sentimiento
 * - J/P: Juicio vs Percepción
 *
 * Resultado: 16 tipos posibles (INTJ, ENFP, ISTP, etc.)
 */

// Escala binaria para cada dimensión
const mbtiScale = (poleA: string, poleB: string) => [
  { label: poleA, value: 0 },
  { label: poleB, value: 1 }
];

export const TESTS_16_PERSONALITY: TestDefinition[] = [
  {
    id: '16pf-adult',
    code: '16PF',
    name: '16 Personality - Test de Personalidad MBTI',
    category: 'Personalidad',
    description: 'Evalúa 4 dimensiones fundamentales de la personalidad basado en la teoría de Carl Jung y el MBTI. Identifica tu tipo de personalidad entre 16 posibles tipos. 60 preguntas.',

    questions: [
      // ============================================
      // DIMENSIÓN E/I (Extraversión vs Introversión)
      // ============================================
      {
        id: 'ei1',
        text: 'En reuniones sociales, prefieres:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Estar en el centro de atención y conocer gente nueva', 'Tener conversaciones profundas con pocos amigos cercanos')
      },
      {
        id: 'ei2',
        text: 'Después de un largo día, te sientes mejor:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Saliendo con amigos y socializando', 'Quedándote en casa a solas para recargar energía')
      },
      {
        id: 'ei3',
        text: 'Cuando tienes tiempo libre, generalmente:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Buscas actividades grupales o eventos sociales', 'Prefieres actividades tranquilas y solitarias')
      },
      {
        id: 'ei4',
        text: 'En el trabajo o escuela, te resulta más fácil:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Trabajar en equipo y colaborar', 'Trabajar de forma independiente')
      },
      {
        id: 'ei5',
        text: 'Cuando conoces a alguien nuevo:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Te resulta fácil empezar una conversación', 'Esperas a que la otra persona inicie')
      },
      {
        id: 'ei6',
        text: 'Tu círculo social es:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Amplio, con muchos conocidos', 'Pequeño, con pocos amigos muy cercanos')
      },
      {
        id: 'ei7',
        text: 'En fiestas o reuniones grandes:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Te sientes energizado y activo', 'Te cansas y necesitas descansos')
      },
      {
        id: 'ei8',
        text: 'Cuando tienes un problema:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Hablas sobre ello con varias personas', 'Prefieres procesarlo solo antes de compartirlo')
      },
      {
        id: 'ei9',
        text: 'Tu forma preferida de comunicarte es:',
        type: QuestionType.LIKERT,
        options: mbtiScale('En persona o por teléfono', 'Por mensajes de texto o correo')
      },
      {
        id: 'ei10',
        text: 'Generalmente piensas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('En voz alta, compartiendo tus ideas', 'Internamente, reflexionando antes de hablar')
      },
      {
        id: 'ei11',
        text: 'Te describirías como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Sociable y expresivo', 'Reservado y reflexivo')
      },
      {
        id: 'ei12',
        text: 'Prefieres vacaciones:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Con mucha gente y actividades sociales', 'Tranquilas y relajadas')
      },
      {
        id: 'ei13',
        text: 'Tu energía aumenta cuando:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Estás rodeado de personas', 'Tienes tiempo a solas')
      },
      {
        id: 'ei14',
        text: 'En situaciones nuevas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Te adaptas rápido y te sientes cómodo', 'Necesitas tiempo para adaptarte')
      },
      {
        id: 'ei15',
        text: 'Las personas te describen como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Extrovertido y hablador', 'Introvertido y callado')
      },

      // ============================================
      // DIMENSIÓN S/N (Sensación vs Intuición)
      // ============================================
      {
        id: 'sn1',
        text: 'Al resolver problemas, confías más en:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Hechos concretos y experiencia práctica', 'Intuición y posibilidades futuras')
      },
      {
        id: 'sn2',
        text: 'Te interesa más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Lo que es real y tangible', 'Lo que podría ser y las ideas abstractas')
      },
      {
        id: 'sn3',
        text: 'Prefieres instrucciones que sean:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Paso a paso y detalladas', 'Generales, dejándote libertad para improvisar')
      },
      {
        id: 'sn4',
        text: 'Te describes como alguien:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Práctico y realista', 'Imaginativo y visionario')
      },
      {
        id: 'sn5',
        text: 'Cuando aprendes algo nuevo:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Prefieres ejemplos concretos', 'Prefieres entender el concepto general')
      },
      {
        id: 'sn6',
        text: 'Al tomar decisiones, te basas en:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Experiencias pasadas', 'Posibilidades futuras')
      },
      {
        id: 'sn7',
        text: 'Disfrutas más de:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Trabajar con cosas tangibles y aplicaciones prácticas', 'Explorar teorías y conceptos')
      },
      {
        id: 'sn8',
        text: 'Te enfocas más en:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Los detalles específicos', 'La visión general')
      },
      {
        id: 'sn9',
        text: 'Confías más en:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Tus cinco sentidos', 'Tu sexto sentido (intuición)')
      },
      {
        id: 'sn10',
        text: 'Al hablar, tiendes a:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Ser literal y específico', 'Usar metáforas y analogías')
      },
      {
        id: 'sn11',
        text: 'Valoras más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('El sentido común', 'La innovación')
      },
      {
        id: 'sn12',
        text: 'Prefieres:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Métodos probados y confiables', 'Nuevas formas de hacer las cosas')
      },
      {
        id: 'sn13',
        text: 'Te interesa más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Cómo funcionan las cosas ahora', 'Cómo podrían mejorar en el futuro')
      },
      {
        id: 'sn14',
        text: 'Al recordar eventos:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Recuerdas detalles precisos', 'Recuerdas impresiones generales')
      },
      {
        id: 'sn15',
        text: 'Te atraen más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Hechos y datos', 'Conceptos e ideas')
      },

      // ============================================
      // DIMENSIÓN T/F (Pensamiento vs Sentimiento)
      // ============================================
      {
        id: 'tf1',
        text: 'Al tomar decisiones importantes, priorizas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Lógica y análisis objetivo', 'Valores personales y el impacto en las personas')
      },
      {
        id: 'tf2',
        text: 'Es más importante para ti ser:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Justo y objetivo', 'Compasivo y empático')
      },
      {
        id: 'tf3',
        text: 'Al dar feedback a alguien:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Eres directo y honesto, aunque pueda ser duro', 'Consideras sus sentimientos y suavizas el mensaje')
      },
      {
        id: 'tf4',
        text: 'Valoras más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('La verdad', 'La armonía')
      },
      {
        id: 'tf5',
        text: 'En discusiones, te enfocas en:',
        type: QuestionType.LIKERT,
        options: mbtiScale('El argumento lógico', 'Cómo se sienten las personas')
      },
      {
        id: 'tf6',
        text: 'Te describirías como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Racional y analítico', 'Emocional y cálido')
      },
      {
        id: 'tf7',
        text: 'Al resolver conflictos:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Buscas la solución más lógica', 'Buscas que todos se sientan bien')
      },
      {
        id: 'tf8',
        text: 'Te mueves más por:',
        type: QuestionType.LIKERT,
        options: mbtiScale('La cabeza (razón)', 'El corazón (emoción)')
      },
      {
        id: 'tf9',
        text: 'Cuando alguien tiene un problema:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Ofreces soluciones prácticas', 'Ofreces apoyo emocional')
      },
      {
        id: 'tf10',
        text: 'Es más fácil para ti:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Ser crítico y objetivo', 'Ser comprensivo y perdonar')
      },
      {
        id: 'tf11',
        text: 'Prefieres que las reglas sean:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Aplicadas de forma consistente', 'Flexibles según la situación personal')
      },
      {
        id: 'tf12',
        text: 'Al evaluar ideas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Analizas pros y contras objetivamente', 'Consideras cómo afecta a las personas involucradas')
      },
      {
        id: 'tf13',
        text: 'Te convencen más:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Argumentos lógicos', 'Apelaciones emocionales')
      },
      {
        id: 'tf14',
        text: 'Las personas te describen como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Objetivo y directo', 'Sensible y empático')
      },
      {
        id: 'tf15',
        text: 'Al trabajar en equipo, priorizas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('La eficiencia y los resultados', 'El bienestar del equipo')
      },

      // ============================================
      // DIMENSIÓN J/P (Juicio vs Percepción)
      // ============================================
      {
        id: 'jp1',
        text: 'Prefieres:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Tener un plan detallado', 'Ser espontáneo y adaptarte sobre la marcha')
      },
      {
        id: 'jp2',
        text: 'Tu espacio de trabajo/estudio suele estar:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Organizado y ordenado', 'Desordenado pero funcional para ti')
      },
      {
        id: 'jp3',
        text: 'Al hacer un proyecto:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Empiezas temprano y lo terminas antes de la fecha límite', 'Trabajas mejor bajo presión, cerca del deadline')
      },
      {
        id: 'jp4',
        text: 'Te sientes mejor cuando:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Las cosas están decididas y establecidas', 'Mantienes tus opciones abiertas')
      },
      {
        id: 'jp5',
        text: 'Prefieres listas de tareas:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Detalladas y que sigues religiosamente', 'Flexibles o no las usas')
      },
      {
        id: 'jp6',
        text: 'Al planear vacaciones:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Haces reservas y un itinerario con anticipación', 'Prefieres decidir en el momento')
      },
      {
        id: 'jp7',
        text: 'Te describes como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Estructurado y metódico', 'Flexible y espontáneo')
      },
      {
        id: 'jp8',
        text: 'Cambios de última hora:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Te estresan', 'Los disfrutas')
      },
      {
        id: 'jp9',
        text: 'Prefieres:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Terminar una tarea antes de empezar otra', 'Trabajar en varias cosas a la vez')
      },
      {
        id: 'jp10',
        text: 'Tu enfoque hacia la vida es:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Planificado y controlado', 'Relajado y adaptable')
      },
      {
        id: 'jp11',
        text: 'Fechas límite:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Las tomas muy en serio', 'Las ves como sugerencias flexibles')
      },
      {
        id: 'jp12',
        text: 'Prefieres reglas que sean:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Claras y bien definidas', 'Flexibles y adaptables')
      },
      {
        id: 'jp13',
        text: 'Te sientes más cómodo:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Con rutinas establecidas', 'Con variedad y cambios')
      },
      {
        id: 'jp14',
        text: 'Al enfrentar decisiones:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Decides rápido para tener certeza', 'Tomas tu tiempo y mantienes opciones abiertas')
      },
      {
        id: 'jp15',
        text: 'Las personas te describen como:',
        type: QuestionType.LIKERT,
        options: mbtiScale('Organizado y responsable', 'Espontáneo y relajado')
      }
    ],

    subscales: [
      {
        id: 'ei',
        name: 'Extraversión (E) / Introversión (I)',
        questionIds: ['ei1', 'ei2', 'ei3', 'ei4', 'ei5', 'ei6', 'ei7', 'ei8', 'ei9', 'ei10', 'ei11', 'ei12', 'ei13', 'ei14', 'ei15'],
        description: 'Puntuación 0-7 = Introversión (I); 8-15 = Extraversión (E)'
      },
      {
        id: 'sn',
        name: 'Sensación (S) / Intuición (N)',
        questionIds: ['sn1', 'sn2', 'sn3', 'sn4', 'sn5', 'sn6', 'sn7', 'sn8', 'sn9', 'sn10', 'sn11', 'sn12', 'sn13', 'sn14', 'sn15'],
        description: 'Puntuación 0-7 = Sensación (S); 8-15 = Intuición (N)'
      },
      {
        id: 'tf',
        name: 'Pensamiento (T) / Sentimiento (F)',
        questionIds: ['tf1', 'tf2', 'tf3', 'tf4', 'tf5', 'tf6', 'tf7', 'tf8', 'tf9', 'tf10', 'tf11', 'tf12', 'tf13', 'tf14', 'tf15'],
        description: 'Puntuación 0-7 = Pensamiento (T); 8-15 = Sentimiento (F)'
      },
      {
        id: 'jp',
        name: 'Juicio (J) / Percepción (P)',
        questionIds: ['jp1', 'jp2', 'jp3', 'jp4', 'jp5', 'jp6', 'jp7', 'jp8', 'jp9', 'jp10', 'jp11', 'jp12', 'jp13', 'jp14', 'jp15'],
        description: 'Puntuación 0-7 = Juicio (J); 8-15 = Percepción (P)'
      }
    ],
    interpretations: [
      // Para el 16 Personality, las interpretaciones son por tipo, no por puntuación total
      // El scoring será especial y se manejará en el componente
      {
        min: 0,
        max: 60,
        label: 'Resultado basado en dimensiones E/I, S/N, T/F, J/P',
        color: 'bg-purple-100 text-purple-800'
      }
    ]
  }
];
